import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Clock, Coffee } from 'lucide-react';

interface ShiftTime {
  startTime: string;
  endTime: string;
  daysOfWeek: string[];
  order: number;
  autoBreak: boolean;
  autoBreakStartTime?: string;
  autoBreakEndTime?: string;
}

interface Shift {
  id: string;
  name: string;
  description?: string;
  isDefault: boolean;
  isActive: boolean;
  lateArrivalToleranceMinutes: number;
  earlyDepartureToleranceMinutes: number;
  earlyArrivalToleranceMinutes: number;
  lateDepartureToleranceMinutes: number;
  shiftTimes: ShiftTime[];
  employeeCount: number;
}

interface ShiftDialogProps {
  shift: Shift | null;
  open: boolean;
  onClose: () => void;
  onSave: (id: string | null, data: any) => Promise<void>;
}

const DAYS_OF_WEEK = [
  { code: 'MON', label: 'Pn' },
  { code: 'TUE', label: 'Wt' },
  { code: 'WED', label: 'Śr' },
  { code: 'THU', label: 'Czw' },
  { code: 'FRI', label: 'Pt' },
  { code: 'SAT', label: 'Sob' },
  { code: 'SUN', label: 'Ndz' },
];

export const ShiftDialog: React.FC<ShiftDialogProps> = ({
  shift,
  open,
  onClose,
  onSave,
}) => {
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isDefault: false,
    lateArrivalToleranceMinutes: 0,
    earlyDepartureToleranceMinutes: 0,
    earlyArrivalToleranceMinutes: 0,
    lateDepartureToleranceMinutes: 0,
    isActive: true,
    shiftTimes: [
      { startTime: '08:00', endTime: '16:00', daysOfWeek: [], order: 0, autoBreak: false, autoBreakStartTime: '', autoBreakEndTime: '' }
    ] as ShiftTime[]
  });

  useEffect(() => {
    if (shift) {
      // Edit mode
      setFormData({
        name: shift.name,
        description: shift.description || '',
        isDefault: shift.isDefault,
        lateArrivalToleranceMinutes: shift.lateArrivalToleranceMinutes,
        earlyDepartureToleranceMinutes: shift.earlyDepartureToleranceMinutes,
        earlyArrivalToleranceMinutes: shift.earlyArrivalToleranceMinutes,
        lateDepartureToleranceMinutes: shift.lateDepartureToleranceMinutes,
        isActive: shift.isActive,
        shiftTimes: shift.shiftTimes.length > 0
          ? shift.shiftTimes.map(st => ({
              startTime: st.startTime,
              endTime: st.endTime,
              daysOfWeek: st.daysOfWeek || [],
              order: st.order,
              autoBreak: st.autoBreak || false,
              autoBreakStartTime: st.autoBreakStartTime || '',
              autoBreakEndTime: st.autoBreakEndTime || '',
            }))
          : [{ startTime: '08:00', endTime: '16:00', daysOfWeek: [], order: 0, autoBreak: false, autoBreakStartTime: '', autoBreakEndTime: '' }]
      });
    } else {
      // Create mode - reset form
      setFormData({
        name: '',
        description: '',
        isDefault: false,
        lateArrivalToleranceMinutes: 10,
        earlyDepartureToleranceMinutes: 10,
        earlyArrivalToleranceMinutes: 10,
        lateDepartureToleranceMinutes: 10,
        isActive: true,
        shiftTimes: [
          { startTime: '08:00', endTime: '16:00', daysOfWeek: [], order: 0, autoBreak: false, autoBreakStartTime: '', autoBreakEndTime: '' }
        ]
      });
    }
    setFormError('');
  }, [shift, open]);

  const handleAddShiftTime = () => {
    setFormData(prev => ({
      ...prev,
      shiftTimes: [
        ...prev.shiftTimes,
        {
          startTime: '08:00',
          endTime: '16:00',
          daysOfWeek: [],
          order: prev.shiftTimes.length,
          autoBreak: false,
          autoBreakStartTime: '',
          autoBreakEndTime: '',
        }
      ]
    }));
  };

  const handleRemoveShiftTime = (index: number) => {
    if (formData.shiftTimes.length === 1) {
      toast.error('Zmianowość musi mieć przynajmniej jeden przedział');
      return;
    }
    setFormData(prev => ({
      ...prev,
      shiftTimes: prev.shiftTimes.filter((_, i) => i !== index)
    }));
  };

  const handleShiftTimeChange = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      shiftTimes: prev.shiftTimes.map((st, i) =>
        i === index ? { ...st, [field]: value } : st
      )
    }));
  };

  const toggleDayOfWeek = (index: number, day: string) => {
    setFormData(prev => ({
      ...prev,
      shiftTimes: prev.shiftTimes.map((st, i) => {
        if (i !== index) return st;
        const daysSet = new Set(st.daysOfWeek);
        if (daysSet.has(day)) {
          daysSet.delete(day);
        } else {
          daysSet.add(day);
        }
        return { ...st, daysOfWeek: Array.from(daysSet) };
      })
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!formData.name.trim()) {
      setFormError('Nazwa zmianowości jest wymagana');
      return;
    }

    // Validate shift times
    for (let i = 0; i < formData.shiftTimes.length; i++) {
      const st = formData.shiftTimes[i];
      const label = `Przedział ${st.startTime}–${st.endTime}`;
      if (!st.startTime || !st.endTime) {
        setFormError(`Przedział ${i + 1}: Godziny są wymagane`);
        return;
      }
      if (st.autoBreak) {
        if (!st.autoBreakStartTime || !st.autoBreakEndTime) {
          setFormError(`${label}: Godziny automatycznej przerwy są wymagane`);
          return;
        }
        if (st.autoBreakStartTime >= st.autoBreakEndTime) {
          setFormError(`${label}: Początek przerwy musi być wcześniej niż koniec`);
          return;
        }
        // For normal shifts (not overnight), validate break is within shift range
        const isOvernightShift = st.endTime <= st.startTime;
        if (!isOvernightShift) {
          if (st.autoBreakStartTime < st.startTime || st.autoBreakEndTime > st.endTime) {
            setFormError(`${label}: Przerwa musi mieścić się w godzinach zmiany`);
            return;
          }
        } else {
          // Overnight shift: break must be in evening part (>= start) or morning part (<= end)
          const breakInEvening = st.autoBreakStartTime >= st.startTime && st.autoBreakEndTime >= st.startTime;
          const breakInMorning = st.autoBreakStartTime <= st.endTime && st.autoBreakEndTime <= st.endTime;
          if (!breakInEvening && !breakInMorning) {
            setFormError(`${label}: Przerwa musi mieścić się w godzinach zmiany`);
            return;
          }
        }
      }
    }

    try {
      setSaving(true);

      const saveData: any = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        isDefault: formData.isDefault,
        lateArrivalToleranceMinutes: formData.lateArrivalToleranceMinutes,
        earlyDepartureToleranceMinutes: formData.earlyDepartureToleranceMinutes,
        earlyArrivalToleranceMinutes: formData.earlyArrivalToleranceMinutes,
        lateDepartureToleranceMinutes: formData.lateDepartureToleranceMinutes,
        shiftTimes: formData.shiftTimes.map((st, idx) => ({
          startTime: st.startTime,
          endTime: st.endTime,
          daysOfWeek: st.daysOfWeek,
          order: idx,
          autoBreak: st.autoBreak,
          autoBreakStartTime: st.autoBreak ? st.autoBreakStartTime : null,
          autoBreakEndTime: st.autoBreak ? st.autoBreakEndTime : null,
        }))
      };

      if (shift) {
        saveData.isActive = formData.isActive;
      }

      await onSave(shift?.id || null, saveData);

      toast.success(shift
        ? `Zmianowość "${formData.name}" została zaktualizowana`
        : `Zmianowość "${formData.name}" została dodana`
      );

      onClose();
    } catch (error: any) {
      console.error('Error saving shift:', error);
      setFormError(error.message || 'Wystąpił błąd podczas zapisywania');
      toast.error('Nie udało się zapisać zmianowości');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {shift ? 'Edytuj zmianowość' : 'Dodaj nową zmianowość'}
          </DialogTitle>
          <DialogDescription>
            {shift
              ? 'Zaktualizuj informacje o zmianowości'
              : 'Wprowadź dane nowej zmianowości'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6 py-4">
            {/* Basic info */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">
                  Nazwa zmianowości <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="np. Zmiana główna"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Opis</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Dodatkowy opis zmianowości"
                  rows={2}
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="isDefault" className="font-normal cursor-pointer">
                  Ustaw jako domyślną zmianowość
                </Label>
              </div>

              {shift && (
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="isActive" className="font-normal cursor-pointer">
                    Aktywna
                  </Label>
                </div>
              )}
            </div>

            {/* Tolerances */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold mb-3">Tolerancje (w minutach)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="lateArrival">Tolerancja spóźnienia</Label>
                  <Input
                    id="lateArrival"
                    type="number"
                    min="0"
                    value={formData.lateArrivalToleranceMinutes}
                    onChange={(e) => setFormData({ ...formData, lateArrivalToleranceMinutes: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label htmlFor="earlyDeparture">Tolerancja wcześniejszego wyjścia</Label>
                  <Input
                    id="earlyDeparture"
                    type="number"
                    min="0"
                    value={formData.earlyDepartureToleranceMinutes}
                    onChange={(e) => setFormData({ ...formData, earlyDepartureToleranceMinutes: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label htmlFor="earlyArrival">Tolerancja wcześniejszego wejścia</Label>
                  <Input
                    id="earlyArrival"
                    type="number"
                    min="0"
                    value={formData.earlyArrivalToleranceMinutes}
                    onChange={(e) => setFormData({ ...formData, earlyArrivalToleranceMinutes: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label htmlFor="lateDeparture">Tolerancja późniejszego wyjścia</Label>
                  <Input
                    id="lateDeparture"
                    type="number"
                    min="0"
                    value={formData.lateDepartureToleranceMinutes}
                    onChange={(e) => setFormData({ ...formData, lateDepartureToleranceMinutes: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
            </div>

            {/* Shift Times */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold">Przedziały czasowe</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddShiftTime}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Dodaj przedział
                </Button>
              </div>

              <div className="space-y-4">
                {formData.shiftTimes.map((shiftTime, index) => (
                  <div key={index} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Przedział {index + 1}</span>
                      </div>
                      {formData.shiftTimes.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveShiftTime(index)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor={`startTime-${index}`}>Od</Label>
                        <Input
                          id={`startTime-${index}`}
                          type="time"
                          value={shiftTime.startTime}
                          onChange={(e) => handleShiftTimeChange(index, 'startTime', e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor={`endTime-${index}`}>Do</Label>
                        <Input
                          id={`endTime-${index}`}
                          type="time"
                          value={shiftTime.endTime}
                          onChange={(e) => handleShiftTimeChange(index, 'endTime', e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Dni tygodnia (puste = wszystkie dni)
                      </Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {DAYS_OF_WEEK.map(day => (
                          <Badge
                            key={day.code}
                            variant={shiftTime.daysOfWeek.includes(day.code) ? "default" : "outline"}
                            className="cursor-pointer"
                            onClick={() => toggleDayOfWeek(index, day.code)}
                          >
                            {day.label}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Auto break per shift time */}
                    <div className="border-t pt-3 mt-3">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`autoBreak-${index}`}
                          checked={shiftTime.autoBreak}
                          onChange={(e) => handleShiftTimeChange(index, 'autoBreak', e.target.checked)}
                          className="w-4 h-4"
                        />
                        <Coffee className="w-4 h-4 text-orange-500" />
                        <Label htmlFor={`autoBreak-${index}`} className="font-normal cursor-pointer text-sm">
                          Automatyczna przerwa śniadaniowa
                        </Label>
                      </div>
                      {shiftTime.autoBreak && (
                        <div className="grid grid-cols-2 gap-3 mt-2 pl-6">
                          <div>
                            <Label htmlFor={`autoBreakStart-${index}`} className="text-xs">Początek przerwy</Label>
                            <Input
                              id={`autoBreakStart-${index}`}
                              type="time"
                              value={shiftTime.autoBreakStartTime}
                              onChange={(e) => handleShiftTimeChange(index, 'autoBreakStartTime', e.target.value)}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor={`autoBreakEnd-${index}`} className="text-xs">Koniec przerwy</Label>
                            <Input
                              id={`autoBreakEnd-${index}`}
                              type="time"
                              value={shiftTime.autoBreakEndTime}
                              onChange={(e) => handleShiftTimeChange(index, 'autoBreakEndTime', e.target.value)}
                              required
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {formError && (
              <div className="text-sm text-red-500 bg-red-50 p-3 rounded">
                {formError}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Anuluj
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Zapisywanie...' : (shift ? 'Zapisz zmiany' : 'Dodaj')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
